import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import {
  Upload, FileText, File, Image, Loader2, AlertCircle, Shield, CheckCircle2,
  Clock, X, FileSpreadsheet,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { useTranslation } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function UploadRequestPage() {
  const { t } = useTranslation();
  const { token } = useParams();
  const [requestData, setRequestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploads, setUploads] = useState({}); // { "Driver's License": { file, status, progress } }
  const [allDone, setAllDone] = useState(false);

  // Fetch request details
  useEffect(() => {
    async function fetchRequest() {
      try {
        const res = await fetch(`${API_URL}/api/documents/request/${token}`);
        if (!res.ok) {
          setError(res.status === 404 ? 'not_found' : 'error');
          return;
        }
        const json = await res.json();
        setRequestData(json);
      } catch {
        setError('network');
      } finally {
        setLoading(false);
      }
    }
    fetchRequest();
  }, [token]);

  const getFileIcon = (mime) => {
    if (!mime) return File;
    if (mime === 'application/pdf') return FileText;
    if (mime.startsWith('image/')) return Image;
    if (mime.includes('spreadsheet') || mime.includes('csv')) return FileSpreadsheet;
    return File;
  };

  const handleFileSelect = (requestedFile, acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    
    // 25MB limit
    if (file.size > 25 * 1024 * 1024) {
      setUploads(prev => ({
        ...prev,
        [requestedFile]: { file: null, status: 'error', error: 'File exceeds 25MB limit' }
      }));
      return;
    }

    setUploads(prev => ({
      ...prev,
      [requestedFile]: { file, status: 'ready', progress: 0 }
    }));
  };

  const removeFile = (requestedFile) => {
    setUploads(prev => {
      const copy = { ...prev };
      delete copy[requestedFile];
      return copy;
    });
  };

  const uploadAll = async () => {
    const filesToUpload = Object.entries(uploads).filter(([_, u]) => u.status === 'ready' && u.file);
    
    for (const [requestedFile, upload] of filesToUpload) {
      try {
        // Update status to uploading
        setUploads(prev => ({
          ...prev,
          [requestedFile]: { ...prev[requestedFile], status: 'uploading', progress: 20 }
        }));

        // Step 1: Get presigned URL via the public request-upload endpoint
        const presignRes = await fetch(`${API_URL}/api/documents/request-upload/${token}/presigned`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileName: upload.file.name,
            contentType: upload.file.type || 'application/octet-stream',
            requested_file_label: requestedFile,
          }),
        });
        
        if (!presignRes.ok) throw new Error('Failed to get upload URL');
        const { presignedUrl, fileKey } = await presignRes.json();

        setUploads(prev => ({
          ...prev,
          [requestedFile]: { ...prev[requestedFile], progress: 50 }
        }));

        // Step 2: Upload to S3
        const putRes = await fetch(presignedUrl, {
          method: 'PUT',
          body: upload.file,
          headers: {
            'Content-Type': upload.file.type || 'application/octet-stream',
            'x-amz-server-side-encryption': 'AES256',
          },
        });
        if (!putRes.ok) throw new Error('Upload failed');

        setUploads(prev => ({
          ...prev,
          [requestedFile]: { ...prev[requestedFile], progress: 80 }
        }));

        // Step 3: Register document
        const registerRes = await fetch(`${API_URL}/api/documents/request-upload/${token}/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileKey,
            original_filename: upload.file.name,
            mime_type: upload.file.type || 'application/octet-stream',
            size: upload.file.size,
            requested_file_label: requestedFile,
          }),
        });
        if (!registerRes.ok) throw new Error('Registration failed');

        setUploads(prev => ({
          ...prev,
          [requestedFile]: { ...prev[requestedFile], status: 'done', progress: 100 }
        }));

      } catch (err) {
        setUploads(prev => ({
          ...prev,
          [requestedFile]: { ...prev[requestedFile], status: 'error', error: err.message, progress: 0 }
        }));
      }
    }

    // Check if all requested files are uploaded
    setAllDone(true);
  };

  const readyCount = Object.values(uploads).filter(u => u.status === 'ready').length;
  const doneCount = Object.values(uploads).filter(u => u.status === 'done').length;
  const uploadingCount = Object.values(uploads).filter(u => u.status === 'uploading').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">{t("uploadRequest.notFound")}</h2>
            <p className="text-muted-foreground text-sm">
              This upload request link may be invalid or has already been completed.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">{t("uploadRequest.error")}</h2>
            <p className="text-muted-foreground text-sm">{t("uploadRequest.errorDesc")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // All done state
  if (allDone && doneCount > 0 && uploadingCount === 0) {
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-card border-b">
          <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <FileText className="h-4 w-4 text-white" />
            </div>
            <span className="font-semibold text-foreground">PocketFiler</span>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-12">
          <Card>
            <CardContent className="pt-10 pb-10 text-center">
              <CheckCircle2 className="h-16 w-16 text-green-500 dark:text-green-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">{t("uploadRequest.uploaded")}</h2>
              <p className="text-muted-foreground">
                {doneCount} document{doneCount !== 1 ? 's' : ''} uploaded successfully.
                {requestData?.requester_name && ` ${requestData.requester_name} has been notified.`}
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <FileText className="h-4 w-4 text-white" />
            </div>
            <span className="font-semibold text-foreground">PocketFiler</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground/60">
            <Shield className="h-3.5 w-3.5" />
            Secure upload
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Request Info */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">{t("uploadRequest.title")}</h1>
          {requestData?.requester_name && (
            <p className="text-muted-foreground mt-1">
              <strong>{requestData.requester_name}</strong> is requesting the following documents from you.
            </p>
          )}
          {requestData?.message && (
            <div className="mt-3 p-3 bg-primary/5 border border-primary/20 rounded-lg text-sm text-primary">
              {requestData.message}
            </div>
          )}
        </div>

        {/* Upload Cards */}
        <div className="space-y-4">
          {requestData?.requested_files?.map((requestedFile, idx) => {
            const upload = uploads[requestedFile];
            const isDone = upload?.status === 'done';
            const isUploading = upload?.status === 'uploading';
            const hasError = upload?.status === 'error';
            const hasFile = upload?.file;

            return (
              <UploadCard
                key={idx}
                label={requestedFile}
                upload={upload}
                isDone={isDone}
                isUploading={isUploading}
                hasError={hasError}
                hasFile={hasFile}
                onFileSelect={(files) => handleFileSelect(requestedFile, files)}
                onRemove={() => removeFile(requestedFile)}
                getFileIcon={getFileIcon}
              />
            );
          })}
        </div>

        {/* Upload Button */}
        {readyCount > 0 && (
          <div className="mt-6">
            <Button
              onClick={uploadAll}
              disabled={uploadingCount > 0}
              className="w-full py-6 text-base"
              size="lg"
            >
              {uploadingCount > 0 ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-5 w-5 mr-2" />
                  Upload {readyCount} Document{readyCount !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </div>
        )}

        <p className="text-center text-xs text-muted-foreground/60 mt-8">
          Powered by <a href="https://pocketfiler.com" className="text-primary hover:underline">PocketFiler</a> — Secure document management
        </p>
      </main>
    </div>
  );
}

// --- Upload Card Component ---
function UploadCard({ label, upload, isDone, isUploading, hasError, hasFile, onFileSelect, onRemove, getFileIcon }) {
  const onDrop = useCallback((accepted) => {
    onFileSelect(accepted);
  }, [onFileSelect]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    disabled: isDone || isUploading,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg'],
      'text/*': ['.txt', '.csv'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
  });

  const FileIcon = hasFile ? getFileIcon(upload.file.type) : Upload;

  return (
    <Card className={`overflow-hidden transition-colors ${isDone ? 'border-green-500/20 bg-green-500/5' : ''} ${hasError ? 'border-red-500/20' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-foreground">{label}</span>
          {isDone && <CheckCircle2 className="h-5 w-5 text-green-500 dark:text-green-400" />}
        </div>

        {isDone ? (
          <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4" />
            {upload.file.name} — Uploaded successfully
          </p>
        ) : hasFile && !isUploading ? (
          <div className="flex items-center gap-3 p-3 bg-background rounded-lg border">
            <FileIcon className="h-5 w-5 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{upload.file.name}</p>
              <p className="text-xs text-muted-foreground/60">
                {(upload.file.size / 1024).toFixed(1)} KB
              </p>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onRemove}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : isUploading ? (
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 bg-background rounded-lg border">
              <Loader2 className="h-5 w-5 text-primary animate-spin shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{upload.file.name}</p>
                <Progress value={upload.progress} className="mt-1 h-1.5" />
              </div>
            </div>
          </div>
        ) : (
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
              ${isDragActive ? 'border-blue-400 bg-primary/5' : 'border-border hover:border-border hover:bg-background'}`}
          >
            <input {...getInputProps()} />
            <Upload className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              {isDragActive ? 'Drop file here' : 'Drag & drop or click to browse'}
            </p>
            <p className="text-xs text-muted-foreground/60 mt-1">{t("uploadRequest.fileTypes")}</p>
          </div>
        )}

        {hasError && (
          <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5" />
            {upload.error || 'Upload failed. Please try again.'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
